import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import AttachFile from '@mui/icons-material/AttachFile';
import NoteAddOutlined from '@mui/icons-material/NoteAddOutlined';
import {
  Box,
  Button,
  FormLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AttachmentField } from '@/components/shared/attachment-field';
import { DatePicker } from '@/components/shared/date-picker';
import { useNotification } from '@/context/notification';
import { useCreateAcademicStatus } from '@/data/learning/academic-situation';
import {
  AcademicStatusType,
  type AcademicSituationDetails,
} from '@/models/learning/academic-situation';
import { statusDropdownLabels, statusTransitions } from '@/packages/academic-hub/components/academic-situation-card/status-transitions';

const formSchema = z.object({
  status_type: z.string().nonempty('Selecione uma categoria.'),
  occurred_at: z.string().nonempty('Informe a data.'),
  description: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

const errorMessages: Record<string, string> = {
  integrated_status_change_blocked:
    'O andamento deste curso é atualizado automaticamente pela plataforma.',
};

const DEFAULT_ERROR_MESSAGE = 'Não foi possível registrar o andamento.';

const parseDate = (value: string) =>
  value ? DateTime.fromFormat(value, 'dd/MM/yyyy') : null;

const toOccurredAtISO = (value: string): string => {
  const date = parseDate(value);

  if (!date?.isValid) return DateTime.now().toISO();

  const now = DateTime.now();

  return date.hasSame(now, 'day')
    ? now.toISO()
    : date.endOf('day').toISO();
};

const FieldLabel = ({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: string;
}) => (
  <FormLabel htmlFor={htmlFor} sx={{ display: 'block', mb: 1 }}>
    <Typography component="span" variant="h3" color="neutral.900">
      {children}
    </Typography>
  </FormLabel>
);

interface AcademicStatusFormProps {
  situation: AcademicSituationDetails;
  defaultExpanded?: boolean;
}

export const AcademicStatusForm = ({
  situation,
  defaultExpanded = false,
}: AcademicStatusFormProps) => {
  const { toast } = useNotification();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [file, setFile] = useState<File | null>(null);
  const { mutate, isPending } = useCreateAcademicStatus();

  const transitionOptions = statusTransitions[situation.current_status].map(
    (statusType) => ({
      value: statusType,
      label: statusDropdownLabels[statusType],
    })
  );

  const studyingOption =
    situation.current_status === AcademicStatusType.InProgress
      ? [
          {
            value: AcademicStatusType.InProgress,
            label: statusDropdownLabels[AcademicStatusType.InProgress],
          },
        ]
      : [];

  const categoryOptions = [...studyingOption, ...transitionOptions];

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      status_type: '',
      occurred_at: DateTime.now().toFormat('dd/MM/yyyy'),
      description: '',
    },
    resolver: zodResolver(formSchema),
  });

  if (categoryOptions.length === 0) return null;

  const collapse = () => {
    setIsExpanded(false);
    setFile(null);
    reset();
  };

  const onSubmit = (data: FormValues) => {
    mutate(
      {
        situationId: situation.id,
        statusType: data.status_type as AcademicStatusType,
        occurredAt: toOccurredAtISO(data.occurred_at),
        description: data.description.trim() || undefined,
        file,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Andamento registrado',
            content: 'A etapa foi adicionada à sua linha do tempo.',
            type: 'success',
          });
          collapse();
        },
        onError: (error) => {
          const data = (
            error as AxiosError<{ error?: string; errors?: string[] }>
          ).response?.data;
          const mapped = data?.error && errorMessages[data.error];

          toast({
            title: 'Erro',
            content: mapped || data?.errors?.[0] || DEFAULT_ERROR_MESSAGE,
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Typography variant="h2" mb={isExpanded ? 1 : 2}>
        Atualizar andamento do curso
      </Typography>

      {!isExpanded ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          onClick={() => setIsExpanded(true)}
          sx={{
            p: 2,
            borderRadius: 2,
            cursor: 'pointer',
            border: ({ palette }) => `1px solid ${palette.neutral[200]}`,
          }}
        >
          <NoteAddOutlined sx={{ color: 'neutral.600' }} />
          <Typography flex={1} color="neutral.500">
            Registre uma nova etapa do seu curso...
          </Typography>
          <AttachFile sx={{ color: 'neutral.500' }} />
        </Stack>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography color="neutral.600" mb={2}>
            Registre uma nova etapa do seu curso para manter sua empresa
            atualizada sobre a sua evolução.
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FieldLabel htmlFor="status_type">
                Como estão os seus estudos?
              </FieldLabel>
              <Controller
                control={control}
                name="status_type"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    id="status_type"
                    select
                    fullWidth
                    error={!!error}
                    helperText={error?.message || ' '}
                    slotProps={{ select: { displayEmpty: true } }}
                  >
                    <MenuItem value="" disabled>
                      Selecione uma categoria
                    </MenuItem>
                    {categoryOptions.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FieldLabel htmlFor="occurred_at">
                Quando isso aconteceu?
              </FieldLabel>
              <Controller
                control={control}
                name="occurred_at"
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    field={field}
                    value={parseDate(field.value)}
                    slotProps={{
                      textField: {
                        id: 'occurred_at',
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message || ' ',
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <FieldLabel htmlFor="description">Descreva brevemente</FieldLabel>
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="description"
                    placeholder="Ex: Concluí o 3º semestre com todas as disciplinas aprovadas"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <FormLabel sx={{ display: 'block', mb: 0.5 }}>
                <Typography component="span" variant="h3" color="neutral.900">
                  Anexos
                </Typography>
              </FormLabel>
              <Typography variant="body2" color="neutral.500" mb={1}>
                Envie documentos que comprovem o andamento.
              </Typography>
              <AttachmentField value={file} onChange={setFile} />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="space-between" mt={3}>
            <Button variant="outlined" onClick={collapse}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" loading={isPending}>
              Salvar
            </Button>
          </Stack>
        </form>
      )}
    </Box>
  );
};
